import { ChangeEvent, useMemo } from 'react';
import { AlertTriangle, Bell, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { Product, TaskType } from '@Models';
import Checkbox from '../components/checkbox';
import StepHeader from '../components/stepHeader';
import type { WorkMixOption } from '../constants';
import {
  ALERT_CEILING_HYBRID_DEFAULT,
  ALERT_CEILING_HYBRID_MAX,
  ALERT_CEILING_HYBRID_MIN,
  getCategoryOptions,
  getTaskTypeName,
  getWorkMixOption,
  SECTION_LABEL_CLASS_NAME,
  WORK_MIX_OPTIONS,
} from '../constants';
import { ScheduleWizardWhatState } from '../scheduleWizard.types';

interface Step2WhatProps {
  taskTypes: TaskType[];
  products: Product[];
  retailers: string[];
  taskTypesMissingCategories: TaskType[];
  value: ScheduleWizardWhatState;
  onChange: (value: ScheduleWizardWhatState) => void;
}

const TASK_TYPE_ORDER = ['shelf', 'pog', 'price', 'promoae', 'promo'];

const Step2What = ({ taskTypes, products, retailers, taskTypesMissingCategories, value, onChange }: Step2WhatProps) => {
  const { t } = useTranslation();

  const orderedTaskTypes = useMemo(() => {
    const indexOf = (code: string) => {
      const index = TASK_TYPE_ORDER.indexOf(code);
      return index === -1 ? TASK_TYPE_ORDER.length : index;
    };
    return [...taskTypes].sort((a, b) => indexOf(a.code) - indexOf(b.code));
  }, [taskTypes]);

  const categoryOptions: string[] = useMemo(() => getCategoryOptions(products, retailers), [products, retailers]);

  const handleCategoryToggle = (category: string): void => {
    const isSelected = value.categories.includes(category);
    const nextCategories = isSelected
      ? value.categories.filter((existing) => existing !== category)
      : [...value.categories, category];
    onChange({ ...value, categories: nextCategories });
  };

  const workMixOption = getWorkMixOption(value.alertCeilingPct);

  const handleWorkMixSelect = (option: WorkMixOption): void => {
    if (option === 'alertsOnly') {
      onChange({ ...value, alertCeilingPct: 100, taskTypeIds: [], categories: [], decideWeekly: false });
      return;
    }
    if (option === 'scheduleOnly') {
      onChange({ ...value, alertCeilingPct: 0 });
      return;
    }
    if (value.alertCeilingPct === 0 || value.alertCeilingPct === 100) {
      onChange({ ...value, alertCeilingPct: ALERT_CEILING_HYBRID_DEFAULT });
    }
  };

  const handleAlertCeilingChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onChange({ ...value, alertCeilingPct: Number(event.target.value) });
  };

  const handleTaskTypeToggle = (taskTypeId: string): void => {
    const isSelected = value.taskTypeIds.includes(taskTypeId);
    const nextIds = isSelected
      ? value.taskTypeIds.filter((id) => id !== taskTypeId)
      : [...value.taskTypeIds, taskTypeId];
    onChange({ ...value, taskTypeIds: nextIds, decideWeekly: false });
  };

  const handleDecideWeeklyToggle = (): void => {
    const next = !value.decideWeekly;
    onChange({ ...value, decideWeekly: next, taskTypeIds: next ? [] : value.taskTypeIds });
  };

  const showAlertsExplain = workMixOption !== 'scheduleOnly';
  const showAlertCeilingSlider = workMixOption === 'both';
  const showTaskSelectors = workMixOption !== 'alertsOnly';

  return (
    <div className="flex flex-col gap-4">
      <StepHeader title={t('Schedule.Wizard.Steps.what')} subtitle={t('Schedule.Wizard.What.SectionSubtitle')} />

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <span className={SECTION_LABEL_CLASS_NAME}>{t('Schedule.Wizard.What.WorkMixLabel')}</span>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {WORK_MIX_OPTIONS.map((option) => {
              const isSelected = workMixOption === option.value;
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => handleWorkMixSelect(option.value)}
                  className={cn(
                    'flex flex-col gap-2 rounded-lg border px-3.5 py-3 text-left transition-colors',
                    isSelected
                      ? 'border-brand-600 bg-brand-50 ring-2 ring-brand-100'
                      : 'border-gray-200 bg-white hover:border-gray-300',
                  )}>
                  <div className="flex items-center justify-between">
                    <span
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-lg border',
                        isSelected
                          ? 'border-brand-200 bg-white text-brand-700'
                          : 'border-gray-200 bg-gray-50 text-gray-400',
                      )}>
                      <Icon size={14} />
                    </span>
                    <span
                      className={cn(
                        'flex h-[18px] w-[18px] items-center justify-center rounded-full border',
                        isSelected ? 'border-brand-600 bg-brand-600 text-white' : 'border-gray-300 bg-white',
                      )}>
                      {isSelected && <Check size={11} strokeWidth={3} />}
                    </span>
                  </div>
                  <span className={cn('text-[13px] font-semibold', isSelected ? 'text-brand-700' : 'text-gray-900')}>
                    {t(option.labelKey)}
                  </span>
                  <span className="text-xs font-normal leading-snug text-gray-500">{t(option.descKey)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {showAlertsExplain && (
          <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] border border-brand-200 bg-brand-50 text-brand-700">
                <Bell size={15} />
              </span>
              <div className="min-w-0 flex-1">
                <span className="block text-[13.5px] font-bold leading-tight text-gray-900">
                  {t('Schedule.Wizard.What.AlertsTitle')}
                </span>
                <span className="mt-1 block text-xs leading-normal text-gray-500">
                  {workMixOption === 'alertsOnly'
                    ? t('Schedule.Wizard.What.AlertsExplainAlertsOnly')
                    : t('Schedule.Wizard.What.AlertsExplainBoth')}
                </span>
              </div>
            </div>

            {showAlertCeilingSlider && (
              <div className="mt-1 flex flex-col gap-1.5 border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between text-xs font-semibold text-gray-700">
                  <span>{t('Schedule.Wizard.What.AlertCeilingDefaultLabel', { pct: value.alertCeilingPct })}</span>
                  <span className="font-normal text-gray-500">
                    {t('Schedule.Wizard.What.AlertCeilingDefaultNote', { remaining: 100 - value.alertCeilingPct })}
                  </span>
                </div>
                <input
                  aria-label={t('Schedule.Wizard.What.AlertCeilingDefaultLabel', { pct: value.alertCeilingPct })}
                  type="range"
                  min={ALERT_CEILING_HYBRID_MIN}
                  max={ALERT_CEILING_HYBRID_MAX}
                  step={5}
                  value={value.alertCeilingPct}
                  onChange={handleAlertCeilingChange}
                  className="h-1 w-full cursor-pointer accent-brand-600"
                />
                <div className="flex items-center justify-between text-[10px] text-gray-400">
                  <span>{ALERT_CEILING_HYBRID_MIN}%</span>
                  <span>{ALERT_CEILING_HYBRID_MAX}%</span>
                </div>
              </div>
            )}
          </div>
        )}

        {showTaskSelectors && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className={SECTION_LABEL_CLASS_NAME}>{t('Schedule.Wizard.What.ScheduledTasksLabel')}</span>
              <span className="text-xs text-gray-500">
                {value.decideWeekly
                  ? t('Schedule.Wizard.What.DecideWeeklyChosenEachWeek')
                  : value.taskTypeIds.length > 0
                  ? t('Schedule.Wizard.What.TaskTypesSelected', { count: value.taskTypeIds.length })
                  : t('Schedule.Wizard.What.TaskTypesNoneSelected')}
              </span>
            </div>

            <div className="overflow-hidden rounded-lg border border-gray-200">
              {orderedTaskTypes.map((taskType, index) => {
                const isChecked = value.taskTypeIds.includes(taskType.id);
                const inputId = `schedule-wizard-task-type-${taskType.id}`;
                return (
                  <label
                    key={taskType.id}
                    htmlFor={inputId}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 text-[13px] transition-colors',
                      index > 0 && 'border-t border-gray-100',
                      value.decideWeekly
                        ? 'cursor-not-allowed opacity-40'
                        : cn('cursor-pointer', isChecked ? 'bg-brand-50' : 'bg-white hover:bg-gray-50'),
                    )}>
                    <Checkbox
                      id={inputId}
                      size="md"
                      checked={isChecked}
                      disabled={value.decideWeekly}
                      onChange={() => handleTaskTypeToggle(taskType.id)}
                    />
                    <span className="flex-1 font-medium text-gray-900">{getTaskTypeName(taskType, t)}</span>
                  </label>
                );
              })}
              <label
                htmlFor="schedule-wizard-decide-weekly"
                className={cn(
                  'flex cursor-pointer items-center gap-3 border-t border-gray-100 px-4 py-3 text-[13px] transition-colors',
                  value.decideWeekly ? 'bg-brand-50' : 'bg-gray-50 hover:bg-gray-100',
                )}>
                <Checkbox
                  id="schedule-wizard-decide-weekly"
                  size="md"
                  checked={value.decideWeekly}
                  onChange={handleDecideWeeklyToggle}
                />
                <span className="flex-1">
                  <span className="block font-medium text-gray-900">{t('Schedule.Wizard.What.DecideWeeklyLabel')}</span>
                  <span className="block text-xs text-gray-500">{t('Schedule.Wizard.What.DecideWeeklyDesc')}</span>
                </span>
              </label>
            </div>

            {value.decideWeekly && (
              <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                {workMixOption === 'scheduleOnly'
                  ? t('Schedule.Wizard.What.DecideWeeklyNoteScheduleOnly')
                  : t('Schedule.Wizard.What.DecideWeeklyNoteBoth')}
              </p>
            )}
          </div>
        )}

        {showTaskSelectors && !value.decideWeekly && (
          <div className="flex flex-col gap-2">
            <span className={SECTION_LABEL_CLASS_NAME}>{t('Schedule.Wizard.What.CategoriesLabel')}</span>
            {categoryOptions.length === 0 ? (
              <p className="text-xs text-gray-400">{t('Schedule.Wizard.What.NoCategories')}</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                {categoryOptions.map((category, index) => {
                  const isChecked = value.categories.includes(category);
                  const inputId = `schedule-wizard-category-${category}`;
                  return (
                    <label
                      key={category}
                      htmlFor={inputId}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 px-4 py-3 text-[13px] transition-colors',
                        index > 0 && 'border-t border-gray-100',
                        isChecked ? 'bg-brand-50' : 'bg-white hover:bg-gray-50',
                      )}>
                      <Checkbox
                        id={inputId}
                        size="md"
                        checked={isChecked}
                        onChange={() => handleCategoryToggle(category)}
                      />
                      <span className="flex-1 font-medium text-gray-900">{category}</span>
                    </label>
                  );
                })}
              </div>
            )}

            {taskTypesMissingCategories.length > 0 && (
              <div className="flex items-center gap-2.5 rounded-lg border border-error-200 bg-error-50 px-4 py-3.5 text-xs text-error-700">
                <AlertTriangle size={16} className="shrink-0" />
                {t('Schedule.Wizard.What.MissingCategoriesWarning', {
                  taskTypes: taskTypesMissingCategories.map((taskType) => getTaskTypeName(taskType, t)).join(', '),
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Step2What;
